
import { useCallback } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from '../services';
import type { SmartCopywritingProject, TargetAudience } from '../types';
import type { UseSmartCopywritingState } from './useSmartCopywritingState';

export const useAudienceLogic = (
    state: UseSmartCopywritingState,
    setState: React.Dispatch<React.SetStateAction<UseSmartCopywritingState>>,
    setLoading: (key: keyof UseSmartCopywritingState['loading'], value: boolean) => void
) => {
    const { message } = App.useApp();

    const analyzeAudiences = useCallback(async (
        productName: string,
        sellingPoints: string[],
        brandTone?: string,
        customInput?: string,
        projectContext?: SmartCopywritingProject
    ) => {
        const currentProject = projectContext || state.project;
        if (!currentProject) return [];

        setLoading('audiences', true);

        setState(prev => ({
            ...prev,
            project: prev.project || currentProject,
            productName: productName,
            audiences: [],
            currentStep: 2,
        }));

        return new Promise<TargetAudience[]>((resolve, reject) => {
            const allAudiences: TargetAudience[] = [];

            smartCopywritingAPI.analyzeAudienceStream(
                currentProject.id,
                {
                    product_name: productName,
                    selling_points: sellingPoints,
                    brand_tone: brandTone,
                    custom_input: customInput,
                },
                {
                    onAudience: (audience) => {
                        const audienceWithSelection = { ...audience, selected: false };
                        allAudiences.push(audienceWithSelection);

                        setState(prev => ({
                            ...prev,
                            audiences: [...prev.audiences, audienceWithSelection],
                        }));

                        console.log(`收到人群: ${audience.name}`);
                    },
                    onComplete: (count) => {
                        setLoading('audiences', false);
                        message.success(`分析完成，推荐 ${count} 个目标人群`);
                        resolve(allAudiences);
                    },
                    onError: (errorMsg) => {
                        setLoading('audiences', false);
                        message.error(`人群分析失败: ${errorMsg}`);

                        if (allAudiences.length > 0) {
                            message.warning(`已获取 ${allAudiences.length} 个人群，可继续操作`);
                            resolve(allAudiences);
                        } else {
                            reject(new Error(errorMsg));
                        }
                    },
                }
            );
        });
    }, [state.project, message, setLoading, setState]);

    const selectAudiences = useCallback((selectedIds: string[]) => {
        setState(prev => ({
            ...prev,
            audiences: prev.audiences.map(aud => ({
                ...aud,
                selected: selectedIds.includes(aud.id)
            }))
        }));
    }, [setState]);

    return { analyzeAudiences, selectAudiences };
};
